import React, { useState } from "react";
import { SelectedIcon } from "../../Icons";
import {
  sanitiseString,
  concatStringFromClassesArrayList,
} from "../../Utils/StringUtils";
import { FilterTag, FilterTagText } from "./Styles/FilterButton";

import type { IconTypes } from "../../Icons";
import {
  LinkButton,
  NavigationButton,
  TertiaryButton,
  TertiaryButtonText,
  TertiaryButtonGhost,
  TertiaryButtonGhostText,
} from "./Styles";

import type { IconPosition } from "../../Types/IconPosition";

export interface Props {
  text?: string;
  html?: React.ReactNode;
  id: string;
  classes?: string;
  iconType?: IconTypes;
  iconPosition?: IconPosition;
  dataTestId: string;
  variants?:
    | Array<
        | "primary"
        | "secondary"
        | "small"
        | "ghost"
        | "disabled"
        | "loader"
        | "filter"
        | "tertiary"
        | "navigation"
        | "link"
      >
    | string;
  type?: "button" | "reset" | "submit" | undefined | string;
  ariaExpanded?: boolean;
  ariaLabel?: string;
  ariaControls?: string;
  assistiveTextPrefix?: string;
  onClick?: (...props: unknown[]) => void;
}

export const Button = (props: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const sanitisedId: string = sanitiseString(props.id);
  const sanitisedDataTestId: string = sanitiseString(props.dataTestId);

  const classList = {
    defaultClass: "ons-btn",
    optionalClasses: [
      props.variants?.includes("secondary") ? "ons-btn--secondary" : null,
      props.variants?.includes("loader")
        ? `ons-btn--loader ${isLoading && "ons-is-loading"}`
        : null,
      props.variants?.includes("small") ? "ons-btn--small" : null,
      props.variants?.includes("disabled") || isDisabled
        ? "ons-btn--disabled"
        : null,
      props.variants?.includes("ghost") ? "ons-btn--ghost" : null,
      props.classes ? props.classes : null,
    ],
  };
  const className = concatStringFromClassesArrayList(classList);

  const getButtonType = (
    type: string | undefined
  ): "button" | "submit" | "reset" => {
    switch (type) {
      case "button":
      case "submit":
      case "reset":
        return type;
      default:
        return "submit";
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    props.variants?.includes("loader") && setIsLoading(true);
    props.variants?.includes("loader") && setIsDisabled(true);
    props.onClick?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.currentTarget.classList.add("active");
      props.variants?.includes("loader") && setIsLoading(true);
      props.variants?.includes("loader") && setIsDisabled(true);
      props.onClick?.();
    }
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.currentTarget.classList.remove("active");
    }
  };

  if (props.variants?.includes("filter")) {
    return (
      <FilterTag
        id={sanitisedId}
        data-testid={sanitisedDataTestId}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        aria-expanded={props.ariaExpanded}
        aria-label={props.ariaLabel}
        aria-controls={props.ariaControls}
        className={className}
        role="button"
      >
        <FilterTagText>
          <span className="ons-u-vh">
            {props.assistiveTextPrefix || "Remove filter: "}
          </span>
          {props.text}
        </FilterTagText>
        <span>
          <SelectedIcon iconType="Close" />
        </span>
      </FilterTag>
    );
  } else if (
    props.variants?.includes("tertiary") &&
    !props.variants?.includes("ghost")
  ) {
    return (
      <TertiaryButton
        id={sanitisedId}
        data-testid={sanitisedDataTestId}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        aria-expanded={props.ariaExpanded}
        aria-label={props.ariaLabel}
        aria-controls={props.ariaControls}
        className={className}
        role="button"
      >
        <TertiaryButtonText>
          {props.assistiveTextPrefix && (
            <span className="ons-u-vh">{props.assistiveTextPrefix}</span>
          )}
          {props.text}
        </TertiaryButtonText>
      </TertiaryButton>
    );
  } else if (
    props.variants?.includes("tertiary") &&
    props.variants?.includes("ghost")
  ) {
    return (
      <TertiaryButtonGhost
        id={sanitisedId}
        data-testid={sanitisedDataTestId}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        aria-expanded={props.ariaExpanded}
        aria-label={props.ariaLabel}
        aria-controls={props.ariaControls}
        className={className}
        role="button"
      >
        <TertiaryButtonGhostText>
          {props.assistiveTextPrefix && (
            <span className="ons-u-vh">{props.assistiveTextPrefix}</span>
          )}
          {props.text}
        </TertiaryButtonGhostText>
      </TertiaryButtonGhost>
    );
  } else if (props.variants?.includes("link")) {
    return (
      <LinkButton
        id={sanitisedId}
        data-testid={sanitisedDataTestId}
        onClick={handleClick}
        aria-label={props.ariaLabel}
        className={className}
      >
        {props.assistiveTextPrefix && (
          <span className="ons-u-vh">{props.assistiveTextPrefix}</span>
        )}
        {props.text}
      </LinkButton>
    );
  } else if (props.variants?.includes("navigation")) {
    return (
      <NavigationButton
        id={sanitisedId}
        data-testid={sanitisedDataTestId}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        aria-expanded={props.ariaExpanded}
        aria-label={props.ariaLabel}
        aria-controls={props.ariaControls}
        className={className}
        role="button"
        $disabled={props.variants?.includes("disabled")}
        disabled={isDisabled || props.variants?.includes("disabled")}
      >
        <span>
          {props.iconType !== undefined && (
            <span className="ons-u-mr-xs" data-testid="icon-before">
              <SelectedIcon iconType={props.iconType} />
            </span>
          )}
          <span>{props.text}</span>
        </span>
      </NavigationButton>
    );
  } else {
    return (
      <button
        id={sanitisedId}
        type={getButtonType(props.type)}
        className={className}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        disabled={isDisabled || props.variants?.includes("disabled")}
        aria-expanded={props.ariaExpanded}
        aria-label={props.ariaLabel}
        aria-controls={props.ariaControls}
        data-testid={sanitisedDataTestId}
        role="button"
      >
        <span className="ons-btn__inner">
          {props.iconType !== undefined && props.iconPosition === "before" && (
            <span className="ons-u-mr-xs" data-testid="icon-before">
              <SelectedIcon iconType={props.iconType} />
            </span>
          )}
          {props.iconPosition !== "only" && (
            <span className="ons-btn__text">
              {props.html || props.text}
              {props.variants?.includes("loader") && isLoading && (
                <SelectedIcon iconType="LoadingIcon" />
              )}
            </span>
          )}
          {props.iconType !== undefined &&
            (props.iconPosition === "after" || props.iconPosition === null) && (
              <span className="ons-u-ml-xs" data-testid="icon-after">
                <SelectedIcon iconType={props.iconType} />
              </span>
            )}
          {props.iconType !== undefined && props.iconPosition === "only" && (
            <>
              <SelectedIcon iconType={props.iconType} />
              <span className="ons-btn__text ons-u-vh@2xs@s">
                <span
                  data-testid={`${sanitisedDataTestId}-hidden-text`}
                  className="ons-u-vh"
                >
                  {props.text || props.html}
                </span>
              </span>
            </>
          )}
        </span>
      </button>
    );
  }
};
